/**
 * Onglet Marketing de l'admin : types de la vue, libellés et budgets,
 * partagés entre le serveur (lib/marketing.ts) et l'écran
 * (admin/src/pages/marketing.tsx). Ce module n'importe aucune dépendance
 * serveur : l'admin le lit tel quel.
 */

/** Types de contenus, dans l'ordre des sections et du filtre de l'écran. */
export const MARKETING_TYPES = ["video", "carousel", "image", "logo", "document"] as const;
export type MarketingType = (typeof MARKETING_TYPES)[number];

export const MARKETING_STATUSES = ["draft", "ready", "published"] as const;
export type MarketingStatus = (typeof MARKETING_STATUSES)[number];

export const MARKETING_CHANNELS = ["linkedin", "site", "youtube", "autre"] as const;
export type MarketingChannel = (typeof MARKETING_CHANNELS)[number];

export const MARKETING_TYPE_LABELS: Record<MarketingType, string> = {
  video: "Vidéo",
  carousel: "Carrousel",
  image: "Visuel",
  logo: "Logo",
  document: "Document",
};

/** Titre de section et libellé du filtre. */
export const MARKETING_SECTION_LABELS: Record<MarketingType, string> = {
  video: "Vidéos",
  carousel: "Carrousels",
  image: "Visuels",
  logo: "Logos",
  document: "Documents",
};

export const MARKETING_STATUS_LABELS: Record<MarketingStatus, string> = {
  draft: "Brouillon",
  ready: "Prêt à publier",
  published: "Publié",
};

/**
 * Ton du point de statut (`StatusDot`) : le mot est toujours affiché à côté.
 * « Prêt » est un état acquis, pas une alerte : point à l'encre, jamais
 * `warning` (« attention, à vérifier ») ; le vert reste au seul « Publié ».
 */
export type MarketingTone = "neutral" | "ink" | "success";
export const MARKETING_STATUS_TONES: Record<MarketingStatus, MarketingTone> = {
  draft: "neutral",
  ready: "ink",
  published: "success",
};

export const MARKETING_CHANNEL_LABELS: Record<MarketingChannel, string> = {
  linkedin: "LinkedIn",
  site: "Site",
  youtube: "YouTube",
  autre: "Autre",
};

/** Libellé du lien vers une publication. */
export const MARKETING_CHANNEL_LINK_LABELS: Record<MarketingChannel, string> = {
  linkedin: "Voir sur LinkedIn",
  site: "Voir sur le site",
  youtube: "Voir sur YouTube",
  autre: "Voir la publication",
};

/** Longueur maximale d'un post LinkedIn, en caractères. */
export const LINKEDIN_POST_MAX = 3000;

/**
 * Budgets du dossier marketing/ (versionné dans git : chaque octet y reste
 * pour toujours). Unités décimales, comme les libellés « 12,9 Mo ».
 * Vérifiés par tests/marketing-catalog.test.ts.
 */
export const MARKETING_FILE_MAX_BYTES = 20_000_000;
export const MARKETING_BUDGET_BYTES = 150_000_000;
export const MARKETING_THUMBNAIL_MAX_BYTES = 300_000;

export type MarketingFileKind = "video" | "image" | "pdf" | "text" | "other";
export type MarketingBackground = "light" | "dark";

/** Un fichier servi par /api/admin/marketing/:id/:file, tel que l'écran l'affiche. */
export type MarketingFileView = {
  key: string;
  label: string;
  /** « MP4 », « PDF », « SVG »… */
  format: string;
  kind: MarketingFileKind;
  /** « 1080 × 1350 », ou null quand rien n'est déclaré. */
  dimensions: string | null;
  /** « 4:5 », seulement pour un rapport connu (à 1 % près). */
  ratio: string | null;
  /** « 28 s · 60 i/s », « 12 pages »… */
  detail: string | null;
  /** « 11,2 Mo », mesuré sur le disque ; null si le fichier manque. */
  size: string | null;
  bytes: number | null;
  usage: string | null;
  background: MarketingBackground;
  /** URL versionnée (`?v=`), servie en ligne. */
  url: string;
  /** Même URL avec `download=1` (Content-Disposition: attachment). */
  downloadUrl: string;
  missing: boolean;
};

export type MarketingItemView = {
  id: string;
  type: MarketingType;
  typeLabel: string;
  title: string;
  description: string;
  usage: string;
  status: MarketingStatus;
  /** « Brouillon », « Prêt à publier », « Publié le 24 oct. 2026 ». */
  statusLabel: string;
  statusTone: MarketingTone;
  /** « Mis à jour le 26 oct. 2026 ». */
  updatedLabel: string;
  /** `linkLabel` : « Voir sur le site », « Voir sur LinkedIn »… */
  publications: Array<{ channelLabel: string; dateLabel: string; url: string | null; linkLabel: string }>;
  /** Vignette : `aspect` au format CSS (« 4 / 5 »). Null si elle manque. */
  thumbnail: { url: string; aspect: string; background: MarketingBackground } | null;
  /** « 56 s · 16:9 · 12,9 Mo ». */
  summary: string;
  /** Texte brut du post LinkedIn, sauts de ligne compris ; `missing` si le fichier déclaré manque. */
  post: { text: string; lengthLabel: string; tooLong: boolean; missing: boolean } | null;
  /** Autres textes à copier : titre de document LinkedIn, texte alternatif… */
  texts: Array<{ label: string; text: string }>;
  slides: MarketingFileView[];
  variants: MarketingFileView[];
};

export type MarketingFilterKey = "all" | MarketingType;

export type MarketingView = {
  stats: {
    items: number;
    itemsNote: string;
    ready: number;
    readyNote: string;
    published: number;
    publishedNote: string;
    drafts: number;
    /** Poids de marketing/ seulement (brand/ et site/public/ sont hors budget). */
    weight: string;
    weightNote: string;
    overBudget: boolean;
  };
  filters: Array<{ key: MarketingFilterKey; label: string; count: number }>;
  items: MarketingItemView[];
  /** Entrées du catalogue écartées, avec la raison : l'écran les liste. */
  problems: string[];
};
